import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { myFirebaseAuth } from '../../firebase/config';
import type {
  RemittanceRecord,
  CashReceipt,
  QuickbooksInvoice,
  AppliedInvoice,
  BankTransactionRecord,
  CashApplicationSettings,
  CashAppEmailIngestionStatus,
} from '@dill/dill-shared';
import type { AppDispatch, RootState } from '../store';

function extractErrorMessage(error: any, fallback: string): string {
  if (error?.response?.data?.errors) {
    return [...error.response.data.errors].reduce((prev, curr) => {
      if (curr.msg) {
        return prev ? `${prev} ${curr.msg}` : curr.msg;
      }
      return prev;
    }, '');
  }
  return error?.message || fallback;
}

export interface ProcessFileResponse {
  payments: {
    payerName: string | null;
    amount: number;
    checkNumber: string | null;
    paymentDate: string | null;
    memo: string | null;
    referenceNumbers: string[];
  }[];
  remittanceRecords: RemittanceRecord[];
}

export const fetchRemittanceRecordsThunk = createAsyncThunk(
  'cashApplication/fetchRemittanceRecords',
  async (params: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return thunkApi.rejectWithValue('Not authenticated');
      }

      const response = await axios.get(
        `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/remittance-records`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.success) {
        return response.data.data as RemittanceRecord[];
      }

      return thunkApi.rejectWithValue('Failed to fetch remittance records');
    } catch (error: any) {
      return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to fetch records'));
    }
  }
);

export const fetchCashReceiptsThunk = createAsyncThunk(
  'cashApplication/fetchCashReceipts',
  async (params: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return thunkApi.rejectWithValue('Not authenticated');
      }

      const response = await axios.get(
        `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/receipts`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.success) {
        return response.data.data as CashReceipt[];
      }

      return thunkApi.rejectWithValue('Failed to fetch cash receipts');
    } catch (error: any) {
      return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to fetch receipts'));
    }
  }
);

export const fetchOpenInvoicesThunk = createAsyncThunk(
  'cashApplication/fetchOpenInvoices',
  async (params: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return thunkApi.rejectWithValue('Not authenticated');
      }

      const response = await axios.get(
        `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/open-invoices`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.success) {
        return response.data.data as QuickbooksInvoice[];
      }

      return thunkApi.rejectWithValue('Failed to fetch open invoices');
    } catch (error: any) {
      return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to fetch invoices'));
    }
  }
);

export const processUploadedFileThunk = createAsyncThunk(
  'cashApplication/processUploadedFile',
  async (
    params: {
      supplierId: string;
      fileUrl: string;
      mimeType: string;
      fileName: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return thunkApi.rejectWithValue('Not authenticated');
      }

      const response = await axios.post(
        `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/process-file`,
        {
          fileUrl: params.fileUrl,
          mimeType: params.mimeType,
          fileName: params.fileName,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.success) {
        return {
          fileName: params.fileName,
          data: response.data.data as ProcessFileResponse,
        };
      }

      return thunkApi.rejectWithValue('Processing failed');
    } catch (error: any) {
      return thunkApi.rejectWithValue(extractErrorMessage(error, 'Processing failed'));
    }
  }
);

/**
 * A posted receipt, alongside the record the caller asked about.
 *
 * The receipt no longer names one record — it lists every record its approval covered — so the id
 * has to come back from the request rather than be read off the response. `pendingAllocations` is
 * keyed by record id, and that is what clears it.
 */
export interface PostedReceiptResult {
  receipt: CashReceipt;
  remittanceId: string;
}

export const applyPaymentThunk = createAsyncThunk<
  PostedReceiptResult,
  {
    supplierId: string;
    remittanceId: string;
    allocations: AppliedInvoice[];
    postMethod: 'AUTO_POST' | 'ONE_CLICK_REVIEW' | 'EXCEPTION_REVIEW';
    /** The operator's agreement to the unallocated remainder posting as open credit. */
    postRemainderAsCredit?: boolean;
  },
  { dispatch: AppDispatch; state: RootState }
>('cashApplication/applyPayment', async (params, thunkApi) => {
  try {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Not authenticated');
    }

    const response = await axios.post(
      `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/apply`,
      {
        remittanceId: params.remittanceId,
        allocations: params.allocations,
        postMethod: params.postMethod,
        postRemainderAsCredit: params.postRemainderAsCredit ?? false,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      const receipt = response.data.data as CashReceipt;
      thunkApi.dispatch(fetchRemittanceRecordsThunk({ supplierId: params.supplierId }));
      thunkApi.dispatch(fetchCashReceiptsThunk({ supplierId: params.supplierId }));
      thunkApi.dispatch(fetchOpenInvoicesThunk({ supplierId: params.supplierId }));
      return { receipt, remittanceId: params.remittanceId };
    }

    return thunkApi.rejectWithValue('Failed to apply payment');
  } catch (error: any) {
    return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to apply payment'));
  }
});

export const postUnappliedThunk = createAsyncThunk<
  PostedReceiptResult,
  {
    supplierId: string;
    remittanceId: string;
    postMethod?: 'AUTO_POST' | 'ONE_CLICK_REVIEW' | 'EXCEPTION_REVIEW';
  },
  { dispatch: AppDispatch; state: RootState }
>('cashApplication/postUnapplied', async (params, thunkApi) => {
  try {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Not authenticated');
    }

    const response = await axios.post(
      `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/post-unapplied`,
      {
        remittanceId: params.remittanceId,
        postMethod: params.postMethod,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      const receipt = response.data.data as CashReceipt;
      thunkApi.dispatch(fetchRemittanceRecordsThunk({ supplierId: params.supplierId }));
      thunkApi.dispatch(fetchCashReceiptsThunk({ supplierId: params.supplierId }));
      return { receipt, remittanceId: params.remittanceId };
    }

    return thunkApi.rejectWithValue('Failed to post unapplied');
  } catch (error: any) {
    return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to post unapplied'));
  }
});

export const approveOneClickThunk = createAsyncThunk<
  PostedReceiptResult,
  {
    supplierId: string;
    remittanceId: string;
    allocations?: AppliedInvoice[];
    /**
     * Only for an approval made with the remainder on screen. Approving a whole deposit leaves it
     * unset, so each member has to carry its own agreement on the record.
     */
    postRemainderAsCredit?: boolean;
  },
  { dispatch: AppDispatch; state: RootState }
>('cashApplication/approveOneClick', async (params, thunkApi) => {
  try {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Not authenticated');
    }

    const response = await axios.post(
      `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/approve-one-click`,
      {
        remittanceId: params.remittanceId,
        allocations: params.allocations,
        postRemainderAsCredit: params.postRemainderAsCredit ?? false,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      const receipt = response.data.data as CashReceipt;
      thunkApi.dispatch(fetchRemittanceRecordsThunk({ supplierId: params.supplierId }));
      thunkApi.dispatch(fetchCashReceiptsThunk({ supplierId: params.supplierId }));
      thunkApi.dispatch(fetchOpenInvoicesThunk({ supplierId: params.supplierId }));
      return { receipt, remittanceId: params.remittanceId };
    }

    return thunkApi.rejectWithValue('Failed to approve payment');
  } catch (error: any) {
    return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to approve payment'));
  }
});

/** Returns the saved settings, plus how many deposits the ignore list cleared on save. */
export const updateCashAppSettingsThunk = createAsyncThunk<
  { settings: CashApplicationSettings; ignoredDismissed: number },
  {
    supplierId: string;
    applyToOldestWhenNoRemittance?: boolean;
    autoPostEnabled?: boolean;
    lockboxEnabled?: boolean;
    ignoredDepositPayers?: string[];
  },
  { dispatch: AppDispatch; state: RootState }
>('cashApplication/updateCashAppSettings', async (params, thunkApi) => {
  try {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Not authenticated');
    }

    const { supplierId, ...changes } = params;
    const response = await axios.patch(
      `${BASE_API_URL}/suppliers/${supplierId}/cashApp/settings`,
      changes,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (response.data?.success) {
      return {
        settings: response.data.data as CashApplicationSettings,
        ignoredDismissed: (response.data.ignoredDismissed as number) ?? 0,
      };
    }

    return thunkApi.rejectWithValue('Failed to save settings');
  } catch (error: any) {
    return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to save settings'));
  }
});

/**
 * Bank feed status as the API returns it. Deliberately not `CashAppBankFeed` — the endpoint
 * withholds `itemId`, `accountId` and `cursor`, which the UI has no use for.
 */
export interface BankFeedStatus {
  linked: boolean;
  enabled?: boolean;
  accountName?: string | null;
  institutionName?: string | null;
  mask?: string | null;
  lastSyncedAt?: string | null;
  lastError?: string | null;
  lastErrorAt?: string | null;
}

export interface BankFeedSyncResult {
  creditsSeen: number;
  settled: number;
  unmatched: number;
  ambiguous: number;
}

export const fetchBankFeedThunk = createAsyncThunk(
  'cashApplication/fetchBankFeed',
  async (params: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return thunkApi.rejectWithValue('Not authenticated');
      }

      const response = await axios.get(
        `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/bank-feed`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.success) {
        return response.data.data as BankFeedStatus;
      }

      return thunkApi.rejectWithValue('Failed to fetch bank feed');
    } catch (error: any) {
      return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to fetch bank feed'));
    }
  }
);

export const createBankFeedLinkTokenThunk = createAsyncThunk(
  'cashApplication/createBankFeedLinkToken',
  async (params: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return thunkApi.rejectWithValue('Not authenticated');
      }

      const response = await axios.post(
        `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/bank-feed/link-token`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.success) {
        return response.data.data.link_token as string;
      }

      return thunkApi.rejectWithValue('Failed to create bank link token');
    } catch (error: any) {
      return thunkApi.rejectWithValue(
        extractErrorMessage(error, 'Failed to create bank link token')
      );
    }
  }
);

export const connectBankFeedThunk = createAsyncThunk(
  'cashApplication/connectBankFeed',
  async (params: { supplierId: string; publicToken: string; accountId?: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return thunkApi.rejectWithValue('Not authenticated');
      }

      const { supplierId, ...body } = params;
      const response = await axios.post(
        `${BASE_API_URL}/suppliers/${supplierId}/cashApp/bank-feed/connect`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.success) {
        return response.data.data as BankFeedStatus;
      }

      return thunkApi.rejectWithValue('Failed to link bank account');
    } catch (error: any) {
      return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to link bank account'));
    }
  }
);

export const disconnectBankFeedThunk = createAsyncThunk(
  'cashApplication/disconnectBankFeed',
  async (params: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return thunkApi.rejectWithValue('Not authenticated');
      }

      const response = await axios.delete(
        `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/bank-feed`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.success) {
        return response.data.data as BankFeedStatus;
      }

      return thunkApi.rejectWithValue('Failed to disconnect bank feed');
    } catch (error: any) {
      return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to disconnect bank feed'));
    }
  }
);

export const syncBankFeedNowThunk = createAsyncThunk(
  'cashApplication/syncBankFeedNow',
  async (params: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return thunkApi.rejectWithValue('Not authenticated');
      }

      const response = await axios.post(
        `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/bank-feed/sync-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.success) {
        return response.data.data as BankFeedSyncResult;
      }

      return thunkApi.rejectWithValue('Failed to sync bank feed');
    } catch (error: any) {
      return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to sync bank feed'));
    }
  }
);

export const fetchEmailIngestionThunk = createAsyncThunk(
  'cashApplication/fetchEmailIngestion',
  async (params: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return thunkApi.rejectWithValue('Not authenticated');
      }

      const response = await axios.get(
        `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/email-ingestion`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.success) {
        return response.data.data as CashAppEmailIngestionStatus;
      }

      return thunkApi.rejectWithValue('Failed to fetch email ingestion settings');
    } catch (error: any) {
      return thunkApi.rejectWithValue(
        extractErrorMessage(error, 'Failed to fetch email ingestion settings')
      );
    }
  }
);

export const enableEmailIngestionThunk = createAsyncThunk(
  'cashApplication/enableEmailIngestion',
  async (params: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return thunkApi.rejectWithValue('Not authenticated');
      }

      const response = await axios.post(
        `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/email-ingestion/enable`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.success) {
        return response.data.data as CashAppEmailIngestionStatus;
      }

      return thunkApi.rejectWithValue('Failed to enable remittance email');
    } catch (error: any) {
      return thunkApi.rejectWithValue(
        extractErrorMessage(error, 'Failed to enable remittance email')
      );
    }
  }
);

export const disableEmailIngestionThunk = createAsyncThunk(
  'cashApplication/disableEmailIngestion',
  async (params: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return thunkApi.rejectWithValue('Not authenticated');
      }

      const response = await axios.post(
        `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/email-ingestion/disable`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.success) {
        return response.data.data as CashAppEmailIngestionStatus;
      }

      return thunkApi.rejectWithValue('Failed to disable remittance email');
    } catch (error: any) {
      return thunkApi.rejectWithValue(
        extractErrorMessage(error, 'Failed to disable remittance email')
      );
    }
  }
);

export interface FetchRemittanceEmailsResult {
  messagesSeen: number;
  processed: number;
  skipped: number;
  failed: number;
  remittanceRecordIds: string[];
}

export const fetchRemittanceEmailsNowThunk = createAsyncThunk<
  FetchRemittanceEmailsResult,
  { supplierId: string; hours?: number },
  { dispatch: AppDispatch; state: RootState }
>('cashApplication/fetchRemittanceEmailsNow', async (params, thunkApi) => {
  try {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Not authenticated');
    }

    const response = await axios.post(
      `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/email-ingestion/fetch-now`,
      { hours: params.hours },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      const result = response.data.data as FetchRemittanceEmailsResult;
      thunkApi.dispatch(fetchRemittanceRecordsThunk({ supplierId: params.supplierId }));
      thunkApi.dispatch(fetchEmailIngestionThunk({ supplierId: params.supplierId }));
      return result;
    }

    return thunkApi.rejectWithValue('Failed to check for new remittance emails');
  } catch (error: any) {
    return thunkApi.rejectWithValue(
      extractErrorMessage(error, 'Failed to check for new remittance emails')
    );
  }
});

/* ------------------------------------------------------------------ */
/* Bank transactions (deposits seen in the feed)                       */
/* ------------------------------------------------------------------ */

export const fetchBankTransactionsThunk = createAsyncThunk<
  BankTransactionRecord[],
  { supplierId: string },
  { dispatch: AppDispatch; state: RootState }
>('cashApplication/fetchBankTransactions', async (params, thunkApi) => {
  try {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Not authenticated');
    }

    const response = await axios.get(
      `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/bank-transactions`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      return response.data.data as BankTransactionRecord[];
    }

    return thunkApi.rejectWithValue('Failed to fetch bank deposits');
  } catch (error: any) {
    return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to fetch bank deposits'));
  }
});

/**
 * Removes a payment from the review queues. Soft on the server — the record is flagged, not erased.
 *
 * Refetches deposits as well as records, which rejecting does not need to: deleting a payment that
 * was linked to a bank credit releases that credit, so the Unmatched Deposits tab changes too.
 * Defined below fetchBankTransactionsThunk for that reason.
 */
export const deleteRemittanceRecordThunk = createAsyncThunk<
  { remittanceId: string; depositUnlinked: boolean },
  { supplierId: string; remittanceId: string },
  { dispatch: AppDispatch; state: RootState }
>('cashApplication/deleteRemittanceRecord', async (params, thunkApi) => {
  try {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Not authenticated');
    }

    const response = await axios.delete(
      `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/remittance-records/${params.remittanceId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      thunkApi.dispatch(fetchRemittanceRecordsThunk({ supplierId: params.supplierId }));
      thunkApi.dispatch(fetchBankTransactionsThunk({ supplierId: params.supplierId }));
      return response.data.data as { remittanceId: string; depositUnlinked: boolean };
    }

    return thunkApi.rejectWithValue('Failed to delete payment');
  } catch (error: any) {
    return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to delete payment'));
  }
});

/**
 * Approve every payment one bank deposit confirms, in one request.
 *
 * Not a loop over `approveOneClickThunk`, and that is the point. The ERP deposit for a bulk-banked
 * cheque file cannot be made until every payment on the credit has posted, so approving them
 * separately leaves the deposit waiting on whichever cheque was left — and a group where some
 * posted and some did not has no good way out. The endpoint validates the whole credit before it
 * writes anything.
 *
 * `allocations` is keyed by remittance id, carrying the edits held in `pendingAllocations`. A
 * member the operator did not touch is absent and posts Dill's suggestion.
 *
 * Refetches the deposits as well as the records: the credit's own match and deposit state changes,
 * and it is what the band reads.
 */
export const approveDepositGroupThunk = createAsyncThunk<
  /** One receipt covering everything that posted. The write is a transaction, so there is no
   * partial outcome to report — either every payment on the deposit was claimed or none was. */
  { receipt: CashReceipt; posted: number },
  {
    supplierId: string;
    bankTransactionId: string;
    allocations?: Record<string, AppliedInvoice[]>;
  },
  { dispatch: AppDispatch; state: RootState }
>('cashApplication/approveDepositGroup', async (params, thunkApi) => {
  try {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Not authenticated');
    }

    const response = await axios.post(
      `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/approve-deposit-group`,
      {
        bankTransactionId: params.bankTransactionId,
        allocations: params.allocations,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      thunkApi.dispatch(fetchRemittanceRecordsThunk({ supplierId: params.supplierId }));
      thunkApi.dispatch(fetchCashReceiptsThunk({ supplierId: params.supplierId }));
      thunkApi.dispatch(fetchOpenInvoicesThunk({ supplierId: params.supplierId }));
      thunkApi.dispatch(fetchBankTransactionsThunk({ supplierId: params.supplierId }));
      return response.data.data as { receipt: CashReceipt; posted: number };
    }

    return thunkApi.rejectWithValue('Failed to approve the payments on this deposit');
  } catch (error: any) {
    return thunkApi.rejectWithValue(
      extractErrorMessage(error, 'Failed to approve the payments on this deposit')
    );
  }
});

export interface ManualAllocationInput {
  invoiceId: string;
  docNumber: string;
  amount: number;
  invoiceAmount?: number | null;
  openBalance?: number | null;
}

export interface ManualRemittanceInput {
  supplierId: string;
  payerName: string;
  amount: number;
  paymentDate?: string | null;
  checkNumber?: string | null;
  memo?: string | null;
  /** Free-text invoice references, matched fuzzily. Ignored when allocations are supplied. */
  invoiceNumbers?: string[];
  buyerId?: string | null;
  /** Invoices the operator chose explicitly. Bypasses matching entirely. */
  allocations?: ManualAllocationInput[];
  sourceFile?: { url: string; name: string; fileType: string } | null;
  /** The operator's own note, which reaches QuickBooks as PrivateNote when this posts. */
  postingNote?: string | null;
  /** When present the new record is settled against this deposit in the same request. */
  bankTransactionId?: string;
}

export const createManualRemittanceThunk = createAsyncThunk<
  { record: RemittanceRecord; settled: boolean },
  ManualRemittanceInput,
  { dispatch: AppDispatch; state: RootState }
>('cashApplication/createManualRemittance', async (params, thunkApi) => {
  try {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Not authenticated');
    }

    const { supplierId, ...payload } = params;

    const response = await axios.post(
      `${BASE_API_URL}/suppliers/${supplierId}/cashApp/remittance-records/manual`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      // Creation runs matching and may settle a deposit, so both sides can move. Refetching is
      // simpler than reasoning about which bucket the new record landed in.
      thunkApi.dispatch(fetchRemittanceRecordsThunk({ supplierId }));
      thunkApi.dispatch(fetchBankTransactionsThunk({ supplierId }));
      return response.data.data as { record: RemittanceRecord; settled: boolean };
    }

    return thunkApi.rejectWithValue('Failed to create the payment record');
  } catch (error: any) {
    return thunkApi.rejectWithValue(
      extractErrorMessage(error, 'Failed to create the payment record')
    );
  }
});

/**
 * Saves an allocation without posting.
 *
 * Distinct from savePendingAllocations, which keeps an edit in the browser for the operator to
 * approve in the same sitting. A record awaiting the bank posts by itself when the deposit lands,
 * so its allocation has to be on the record by then or the edit is lost.
 */
export const updateRemittanceAllocationsThunk = createAsyncThunk<
  { remittanceId: string; allocated: number; unapplied: number },
  {
    supplierId: string;
    remittanceId: string;
    allocations: {
      invoiceId: string;
      docNumber: string;
      amount: number;
      invoiceAmount?: number | null;
      openBalance?: number | null;
    }[];
    /**
     * Whether the operator agreed to the remainder posting as open credit. The server records the
     * remainder it computes rather than a figure from here, and posting is refused later without
     * it — so an allocation that does not add up is saved, but not silently postable.
     */
    postRemainderAsCredit?: boolean;
  },
  { dispatch: AppDispatch; state: RootState }
>('cashApplication/updateRemittanceAllocations', async (params, thunkApi) => {
  try {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Not authenticated');
    }

    const { supplierId, ...payload } = params;

    const response = await axios.patch(
      `${BASE_API_URL}/suppliers/${supplierId}/cashApp/remittance-records/allocations`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      // Only the remittance side moves — no deposit is touched, so the bank feed is left alone.
      thunkApi.dispatch(fetchRemittanceRecordsThunk({ supplierId }));
      return response.data.data as { remittanceId: string; allocated: number; unapplied: number };
    }

    return thunkApi.rejectWithValue('Failed to save the allocation');
  } catch (error: any) {
    return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to save the allocation'));
  }
});

/**
 * Name the Dill customer a payment belongs to.
 *
 * Refreshes the records afterwards rather than patching the store, because assigning a customer is
 * what makes a record postable — the queues re-evaluate that from the record itself.
 */
export const assignRemittanceBuyerThunk = createAsyncThunk<
  { remittanceId: string; buyerId: string; buyerName: string | null },
  { supplierId: string; remittanceId: string; buyerId: string },
  { dispatch: AppDispatch; state: RootState }
>('cashApplication/assignRemittanceBuyer', async (params, thunkApi) => {
  try {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Not authenticated');
    }

    const { supplierId, ...payload } = params;

    const response = await axios.patch(
      `${BASE_API_URL}/suppliers/${supplierId}/cashApp/remittance-records/buyer`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      thunkApi.dispatch(fetchRemittanceRecordsThunk({ supplierId }));
      return response.data.data as {
        remittanceId: string;
        buyerId: string;
        buyerName: string | null;
      };
    }

    return thunkApi.rejectWithValue('Failed to assign the customer');
  } catch (error: any) {
    return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to assign the customer'));
  }
});

/**
 * Save the operator's note on a payment that has not posted yet.
 *
 * Saved to the record rather than held with the pending allocations, because the two are answered
 * at different times: an Awaiting Bank payment posts itself when the deposit lands, so a note kept
 * in the browser would never reach QuickBooks.
 */
export const updateRemittanceNoteThunk = createAsyncThunk<
  { remittanceId: string; postingNote: string | null },
  { supplierId: string; remittanceId: string; postingNote: string | null },
  { dispatch: AppDispatch; state: RootState }
>('cashApplication/updateRemittanceNote', async (params, thunkApi) => {
  try {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Not authenticated');
    }

    const { supplierId, ...payload } = params;

    const response = await axios.patch(
      `${BASE_API_URL}/suppliers/${supplierId}/cashApp/remittance-records/note`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      thunkApi.dispatch(fetchRemittanceRecordsThunk({ supplierId }));
      return response.data.data as { remittanceId: string; postingNote: string | null };
    }

    return thunkApi.rejectWithValue('Failed to save the note');
  } catch (error: any) {
    return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to save the note'));
  }
});

/**
 * Re-run the ERP leg of a post that failed.
 *
 * Returns the receipt as it stands after re-dispatch — POSTING when the job was queued, FAILED
 * again when it could not be. Either way the caller refreshes the list, because the outcome that
 * matters arrives from the worker a few seconds later and the poll in Post History picks it up.
 */
export const retryPostThunk = createAsyncThunk<
  CashReceipt,
  { supplierId: string; receiptId: string },
  { dispatch: AppDispatch; state: RootState }
>('cashApplication/retryPost', async (params, thunkApi) => {
  try {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Not authenticated');
    }

    const response = await axios.post(
      `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/receipts/${params.receiptId}/retry`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      return response.data.data as CashReceipt;
    }

    return thunkApi.rejectWithValue('Failed to retry the post');
  } catch (error: any) {
    return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to retry the post'));
  }
});

/**
 * Deposit a receipt whose payments posted but were never deposited.
 *
 * Separate from `retryPostThunk` because it must not re-send the payments: they already exist in the
 * accounting system, and this only creates the deposit that groups them.
 */
export const retryDepositThunk = createAsyncThunk<
  CashReceipt,
  { supplierId: string; receiptId: string },
  { dispatch: AppDispatch; state: RootState }
>('cashApplication/retryDeposit', async (params, thunkApi) => {
  try {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Not authenticated');
    }

    const response = await axios.post(
      `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/receipts/${params.receiptId}/retry-deposit`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      return response.data.data as CashReceipt;
    }

    return thunkApi.rejectWithValue('Failed to retry the deposit');
  } catch (error: any) {
    return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to retry the deposit'));
  }
});

/**
 * Links one deposit to the payments it covers.
 *
 * `remittanceIds` is a list because a bulk check deposit confirms a whole file of checks at once,
 * and settling one of them at a time is impossible when the deposit's amount only makes sense as
 * their sum.
 */
export const linkBankTransactionThunk = createAsyncThunk<
  { settled: number },
  { supplierId: string; bankTransactionId: string; remittanceIds: string[] },
  { dispatch: AppDispatch; state: RootState }
>('cashApplication/linkBankTransaction', async (params, thunkApi) => {
  try {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Not authenticated');
    }

    const response = await axios.post(
      `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/bank-transactions/link`,
      {
        bankTransactionId: params.bankTransactionId,
        remittanceIds: params.remittanceIds,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      // Linking settles and may post, so both sides of the reconciliation move.
      thunkApi.dispatch(fetchBankTransactionsThunk({ supplierId: params.supplierId }));
      thunkApi.dispatch(fetchRemittanceRecordsThunk({ supplierId: params.supplierId }));
      return response.data.data as { settled: number };
    }

    return thunkApi.rejectWithValue('Failed to link the deposit');
  } catch (error: any) {
    return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to link the deposit'));
  }
});

/**
 * Detaches one payment from a deposit, or every payment when `remittanceId` is omitted.
 *
 * Named rather than always-all: a deposit covering several checks is usually wrong about one of
 * them, and unlinking the rest would undo confirmations nobody disputed.
 */
export const unlinkBankTransactionThunk = createAsyncThunk<
  { unlinked: number; skippedApplied: string[]; stillLinked: string[] },
  { supplierId: string; bankTransactionId: string; remittanceId?: string },
  { dispatch: AppDispatch; state: RootState }
>('cashApplication/unlinkBankTransaction', async (params, thunkApi) => {
  try {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Not authenticated');
    }

    const response = await axios.post(
      `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/bank-transactions/unlink`,
      { bankTransactionId: params.bankTransactionId, remittanceId: params.remittanceId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      thunkApi.dispatch(fetchBankTransactionsThunk({ supplierId: params.supplierId }));
      thunkApi.dispatch(fetchRemittanceRecordsThunk({ supplierId: params.supplierId }));
      return response.data.data as {
        unlinked: number;
        skippedApplied: string[];
        stillLinked: string[];
      };
    }

    return thunkApi.rejectWithValue('Failed to unlink the deposit');
  } catch (error: any) {
    return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to unlink the deposit'));
  }
});

export const dismissBankTransactionThunk = createAsyncThunk<
  { bankTransactionId: string },
  { supplierId: string; bankTransactionId: string; reason?: string },
  { dispatch: AppDispatch; state: RootState }
>('cashApplication/dismissBankTransaction', async (params, thunkApi) => {
  try {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Not authenticated');
    }

    const response = await axios.post(
      `${BASE_API_URL}/suppliers/${params.supplierId}/cashApp/bank-transactions/dismiss`,
      {
        bankTransactionId: params.bankTransactionId,
        reason: params.reason,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      return { bankTransactionId: params.bankTransactionId };
    }

    return thunkApi.rejectWithValue('Failed to dismiss the deposit');
  } catch (error: any) {
    return thunkApi.rejectWithValue(extractErrorMessage(error, 'Failed to dismiss the deposit'));
  }
});
